import { useState } from 'react'

import {
  ExpandMore as ExpandMoreIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material'
import { Alert, Box, Button, Card, CardContent, Collapse, Typography } from '@mui/material'

import { buildUspsZipCodeForAddressRequest } from '../../USPSLocationFinder.functions'
import { formatHoursForDisplay } from './LocationCard.functions'
import styles from './LocationCard.styles'
import { LocationCardProps } from './LocationCard.types'

export default function LocationCard({ location, isSelected, isDisabled = false, onSelect }: LocationCardProps) {
  const [showHours, setShowHours] = useState(false)

  const formattedHours = location.hours ? formatHoursForDisplay(location.hours) : []

  return (
    <Card variant="outlined" sx={styles.card(isSelected, isDisabled)} onClick={isDisabled ? undefined : onSelect}>
      <CardContent>
        <Box sx={styles.header}>
          <Typography variant="subtitle1" component="div" sx={styles.locationName}>
            {location.name}
          </Typography>
        </Box>

        <Box sx={styles.addressRow}>
          <LocationIcon fontSize="small" color="action" sx={styles.icon} />
          <Typography variant="body2" color="text.secondary" sx={styles.addressText}>
            {location.address}, {location.city}, {location.state} {buildUspsZipCodeForAddressRequest(location)}
          </Typography>
        </Box>

        {location.phone && (
          <Box sx={styles.phoneRow}>
            <PhoneIcon fontSize="small" color="action" sx={styles.icon} />
            <Typography variant="body2" color="text.secondary" sx={styles.phoneText}>
              {location.phone}
            </Typography>
          </Box>
        )}

        {formattedHours.length > 0 && (
          <Box>
            <Button
              size="small"
              startIcon={<ScheduleIcon />}
              endIcon={<ExpandMoreIcon sx={styles.expandIcon(showHours)} />}
              onClick={(e) => {
                e.stopPropagation()
                setShowHours(!showHours)
              }}
              sx={styles.hoursButton}
            >
              Opening Hours
            </Button>

            <Collapse in={showHours}>
              <Box sx={styles.hoursContainer}>
                {formattedHours.map(([day, hours]) => (
                  <Box key={day} sx={styles.hoursRow}>
                    <Typography variant="body2" sx={styles.dayLabel}>
                      {day}:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {hours}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>
        )}

        {location.isClosed && (
          <Alert severity="warning" sx={styles.closedAlert}>
            This location is temporarily closed
          </Alert>
        )}

        {isDisabled && (
          <Alert severity="error" sx={styles.closedAlert}>
            This location&apos;s address could not be validated
          </Alert>
        )}
      </CardContent>
    </Card>
  )
}
